import React, { useState, useEffect } from 'react';
import './App.css';
import LoadingScreen from './components/LoadingScreen';
import VotingScreen from './components/VotingScreen';
import ConfirmScreen from './components/ConfirmScreen';
import MaintenanceScreen from './components/MaintenanceScreen';
import CTAModal from './components/CTAModal';
import ErrorScreen from './components/ErrorScreen';
import Footer from './components/Footer';
import TermsConditions from './components/TermsConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import useSupabase from './supabase/useSupabase';
import useUnifiedAnalytics from './analytics/useUnifiedAnalytics';
import { EVENTS } from './analytics/events';

function App() {
  // App state to manage which screen to show
  const [currentScreen, setCurrentScreen] = useState('loading');
  const [selectedContestant, setSelectedContestant] = useState(null);
  const [voterInfo, setVoterInfo] = useState({ name: '', email: '' });
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showCTAModal, setShowCTAModal] = useState(false);
  const [previousScreen, setPreviousScreen] = useState(null); // Track previous screen for back navigation

  // Get Supabase and Nova config from the URL parameter
  const { isAuthenticated, isLoading, pollConfig, siteConfig, error } = useSupabase();

  // Initialize analytics
  const analytics = useUnifiedAnalytics();

  // Track screen changes directly using screen names
  useEffect(() => {
    // Track the current screen directly without using page paths
    if (currentScreen) {
      analytics.trackEvent(EVENTS.PAGE_VIEW, currentScreen);
    }
  }, [currentScreen, analytics]);

  // Use effect to handle loading state based on Supabase auth and config
  useEffect(() => {
    if (isLoading) {
      // Keep showing loading screen while Supabase is initializing and fetching config
      setCurrentScreen('loading');
      console.log('Loading Nova configuration...');
    } else {
      // Once loading is complete, determine which screen to show
      if (siteConfig?.site_status === 'maintenance') {
        setCurrentScreen('maintenance');
        console.log('Site is in maintenance mode');
      } else if (pollConfig) {
        // If we have Nova config, show voting screen
        setCurrentScreen('voting');
        console.log('Nova config loaded, showing voting screen');
      } else if (error) {
        // If there's an error, show error screen
        console.error('Error loading Nova config:', error);
        setCurrentScreen('error');
      } else {
        // If authenticated but no config (no vote_id in URL)
        console.log('No Nova config found, showing error screen');
        setCurrentScreen('error');
      }
    }
  }, [isLoading, pollConfig, error, isAuthenticated]);

  // Handler for when a contestant is selected
  const handleSelectContestant = (contestant) => {
    setSelectedContestant(contestant);
    setCurrentScreen('confirm');
  };

  // Handler for when the vote is submitted
  const handleVoteSubmit = (voteData) => {
    // In a real app, this would send data to a backend
    console.log('Vote submitted:', voteData);
    
    // Show CTA modal
    setShowCTAModal(true);
  };

  // Handler for closing the CTA modal
  const handleCloseCTAModal = () => {
    setShowCTAModal(false);
    setCurrentScreen('voting');
    setSelectedContestant(null);
  };
  
  // Handler for navigating to Terms and Conditions
  const handleNavigateToTerms = () => {
    setPreviousScreen(currentScreen);
    setCurrentScreen('terms');
  };
  
  // Handler for navigating to Privacy Policy
  const handleNavigateToPrivacy = () => {
    setPreviousScreen(currentScreen);
    setCurrentScreen('privacy');
  };
  
  // No need to expose to window, we'll pass these as props
  
  // Handler for returning from Terms or Privacy pages
  const handleBackFromLegal = () => {
    setCurrentScreen(previousScreen || 'voting');
  };

  // Render the appropriate screen based on the current state
  const renderScreen = () => {
    switch (currentScreen) {
      case 'loading':
        return <LoadingScreen />;
      case 'voting':
        return (
          <VotingScreen 
            onSelectContestant={handleSelectContestant} 
            selectedContestant={selectedContestant}
            onSubmitClick={handleVoteSubmit}
            pollConfig={pollConfig}
            siteConfig={siteConfig}
          />
        );
      case 'confirm':
        return (
          <ConfirmScreen 
            contestant={selectedContestant}
            onBack={handleSelectContestant}
            onSubmit={handleVoteSubmit}
            pollConfig={pollConfig}
            siteConfig={siteConfig}
            onNavigateToTerms={handleNavigateToTerms}
            onNavigateToPrivacy={handleNavigateToPrivacy}
          />
        );
      case 'error':
        return <ErrorScreen error={error} />;
      case 'maintenance':
        return <MaintenanceScreen config={siteConfig} />;
      case 'terms':
        return <TermsConditions onBack={handleBackFromLegal} />;
      case 'privacy':
        return <PrivacyPolicy onBack={handleBackFromLegal} />;
      default:
        return null;
    }
  };

  return (
    <div className="App">
      {showCTAModal ? (
        <CTAModal 
          contestant={selectedContestant} 
          onClose={handleCloseCTAModal}
          pollConfig={pollConfig}
        />
      ) : null}
      
      {/* Show the current screen */}
      {renderScreen()}

      {/* Global Footer */}
      <Footer config={siteConfig} />
    </div>
  );
}

export default App;
